import { Winner } from "../types";
import WinnerCardWrapper from "./WinnerCardWrapper";
import WinnerCardTitle from "./WinnerCardTitle";
import { getFormattedDate } from "../category-utils";

interface WinnerCardProps {
  winner: Winner;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "auto" | "low";
}

export default function WinnerCard({ winner, loading = "lazy", fetchPriority = "auto" }: WinnerCardProps) {
  if (winner.SuppressResults === "True") {
    return null;
  }

  const photo = winner.Photo || winner.PlaceholderWinnerImageUrl;
  const competitionTitle = winner.CompetitionPictureTitle;
  const winnerName = winner.Name;
  const publishDate = getFormattedDate(winner);

  return (
    <WinnerCardWrapper winner={winner}>
      <div className="relative w-full bg-[#F1F1F1]">
        <picture>
          {winner.MobilePhoto && <source media="(max-width: 600px)" srcSet={winner.MobilePhoto} />}
          <img
            src={photo}
            alt={winnerName || "winner photo"}
            width={640}
            height={360}
            loading={loading}
            fetchPriority={fetchPriority}
            className="block w-full aspect-[16/9] object-cover"
          />
        </picture>
      </div>

      <div className="flex flex-col flex-1 px-3 lg:px-4 pt-2.5 lg:pt-4 pb-4 lg:pb-6 gap-4">
        <div className="flex flex-col gap-1">
          <WinnerCardTitle
            competitionTitle={competitionTitle}
            winnerName={winnerName}
            isSubscriberCompetition={winner.isSubscriberCompetition}
          />

          {!winner.isSubscriberCompetition ? (
            <p className="tracking-[0] font-jost text-sm lg:text-base font-normal leading-5 lg:leading-[22px] text-secondary line-clamp-2 min-h-10 lg:min-h-11">
              {winner.Prize}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between mt-auto gap-2 min-h-9 lg:min-h-12">
          {publishDate && (
            <span className="font-jost text-xs lg:text-base font-medium leading-4 lg:leading-[22px] text-gray-5">
              {publishDate}
            </span>
          )}

          {winner.Url && (
            <a
              href={winner.Url}
              className="inline-flex items-center gap-1.5 lg:gap-2 shrink-0 font-jost text-sm lg:text-base font-medium leading-5 lg:leading-[22px] text-primary hover:text-[#FF6A00] transition-colors">
              View Results
              <svg
                className="shrink-0 size-4 lg:size-6"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true">
                <path
                  d="m9 6 6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          )}
        </div>
      </div>
    </WinnerCardWrapper>
  );
}
